<template>
  <div class="base-input" :class="containerClasses">
    <label :for="inputId">{{ $props.label }}</label>
    <div class="input-container">
      <input :id="inputId"
             :name="inputName"
             :type="inputType"
             v-bind="$attrs"
             ref="input"
             :disabled="$props.disabled"
             :class="[{'invalid': $props.invalid, 'has-password-button': revealPasswordButtonVisible}, inputClasses]"
             @input="handleInput"
             @focus="handleFocus"
             @blur="handleBlur"
             @change="handleChange"/>
      <div class="input-button" v-if="$slots['input-button']">
        <slot name="input-button"></slot>
      </div>
      <button class="show-password-btn"
              type="button"
              tabindex="-1"
              :disabled="!inputFilled"
              v-if="revealPasswordButtonVisible"
              aria-label="Show password"
              @click.prevent.stop="togglePasswordVisibility">
        <i :class="passwordVisible ? 'icon-sc-eye-on' : 'icon-sc-eye-off'"></i>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'base-input',
  data () {
    return {
      passwordVisible: false,
      inputFilled: false,
      focused: false // will be used if we need clear button later
    }
  },
  inheritAttrs: false,
  props: {
    value: [String, Number],
    id: {
      type: String,
      default: '',
      validator: (val) => {
        const allowedChars = new RegExp('^[a-zA-Z0-9_-]*$', 'g')
        return allowedChars.test(val) && val.length < 50
      }
    },
    name: {
      type: String,
      default: '',
      validator: (val) => {
        const allowedChars = new RegExp('^[a-zA-Z0-9_-]*$', 'g')
        return allowedChars.test(val) && val.length < 50
      }
    },
    type: {
      type: String,
      default: 'text'
    },
    label: String,
    containerClass: String,
    invalid: Boolean,
    revealPassword: Boolean,
    variant: {
      type: String,
      default: 'grey',
      validator: (variant) => {
        const allowedVariants = ['grey', 'white', 'white-bold', 'transparent', 'small', 'center-grey']
        return allowedVariants.includes(variant)
      }
    },
    disabled: [String, Boolean]
  },
  computed: {
    nativeInputValue () {
      return this.$props.value === null || this.$props.value === undefined ? '' : String(this.$props.value)
    },
    inputId () {
      return this.$props.id || `input_${this._uid}`
    },
    inputName () {
      return this.$props.name || this.inputId
    },
    inputType () {
      return this.inputFilled && this.passwordVisible ? 'text' : this.$props.type
    },
    containerClasses () {
      return {
        [this.$props.containerClass]: this.$props.containerClass,
        disabled: this.$props.disabled
      }
    },
    inputClasses () {
      return {
        [`variant-${this.$props.variant}`]: this.$props.variant,
        invalid: this.$props.invalid
      }
    },
    revealPasswordButtonVisible () {
      return !this.$slots['input-button'] && this.$props.revealPassword
    }
  },
  watch: {
    nativeInputValue () {
      this.setNativeInputValue()
    },
    value (newVal) {
      if (!newVal) this.inputFilled = false
    }
  },
  methods: {
    setNativeInputValue () {
      const input = this.$refs.input
      if (!input) return
      if (input.value === this.nativeInputValue) {
        return
      }
      input.value = this.nativeInputValue
    },
    handleInput (event) {
      // IE fix
      if (event.target.value === this.nativeInputValue) return
      this.$emit('input', event.target.value)
      this.$nextTick(this.setNativeInputValue)
      this.inputFilled = !!event.target.value
    },
    handleFocus (event) {
      this.focused = true
      this.$emit('focus', event)
    },
    handleBlur (event) {
      this.focused = false
      this.$emit('blur', event)
    },
    handleChange (event) {
      this.$emit('change', event.target.value)
    },
    togglePasswordVisibility () {
      this.passwordVisible = !this.passwordVisible
      this.$emit('reveal-password', this.passwordVisible)
    },
    focusIn () {
      this.$refs.input.focus()
    },
    focusOut () {
      this.$refs.input.blur()
    },
    selectText () {
      this.$refs.input.select()
    }
  },
  mounted () {
    this.setNativeInputValue()
  }
}
</script>

<style scoped lang="scss" src="./base-comp-styles/base-input.scss"></style>
